// WHAT: Custom hook for managing TimeTracQ workspace configuration loading and saving
// WHY:  Centralizes logic for fetching, sorting, and persisting workspace settings and aspects
// DOMAIN: Settings → TimeTracQ
// DEPENDENCIES: React hooks, Tauri core API, WorkspaceConfig type
// RISKS:
//   - WARN: Hardcoded workspace order may not match user preferences
//   - WARN: Saves entire config object - may overwrite other configuration sections
//   - NOTE: Debug commands are called but results not used - may be unnecessary

import type { WorkspaceConfig, AspectConfig } from "../types";

import { core } from "@tauri-apps/api";
import { useState, useCallback } from "react";

/**
 * WHAT:
 *   Custom hook to manage the loading, saving, and state of the TimeTracQ workspace configuration.
 *
 * WHY:
 *   Encapsulates the data fetching and persistence logic, separating it from the UI component
 *   and making the configuration management reusable and testable.
 *
 * RETURNS:
 *   An object containing the configuration state, loading/saving status, message state,
 *   and functions to load and save the configuration, and load workspace names.
 *
 * @returns {object} Object with `config`, `loading`, `saving`, `message`, `workspaceNames`, `loadConfig`, `saveConfig`, `loadWorkspaceNames`, `setConfig`, `setMessage`, `setSaving`
 *
 * RISKS:
 *   - WARN: Workspace order is hardcoded within `loadConfig` and may not match user preferences.
 *   - WARN: `saveConfig` saves the entire configuration object, potentially overwriting other sections if not handled carefully.
 *   - NOTE: Debug commands are invoked in `loadConfig` but their results are not explicitly used, which might be unnecessary overhead.
 *
 */
export function useWorkspaceConfig() {
  const [config, setConfig] = useState<null | WorkspaceConfig>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<null | {
    type: "error" | "success";
    text: string;
  }>(null);
  const [workspaceNames, setWorkspaceNames] = useState<string[]>([]);

  /**
   * WHAT:
   *   Loads workspace names from backend.
   *
   * WHY:
   *   Provides list of available workspaces for selection in modals.
   */
  const loadWorkspaceNames = useCallback(async () => {
    try {
      const names = await core.invoke<string[]>("get_workspace_names_cmd");
      setWorkspaceNames(names);
    } catch {
      // NOTE: Error loading workspace names - silently fail
    }
  }, []);

  /**
   * WHAT:
   *   Loads workspace configuration from the database and applies predefined sorting order.
   *
   * WHY:
   *   Retrieves configuration data and organizes workspaces in a consistent order for better user experience.
   *
   * SIDE EFFECTS:
   *   - Invokes debug commands for diagnostics.
   *   - Fetches full configuration from the database.
   *   - Extracts settings and workspaces from a flat structure.
   *   - Sorts workspaces according to a hardcoded order.
   *   - Updates component state with the organized configuration.
   *
   * RISKS:
   *   - WARN: The workspace order is hardcoded; it may not match user preferences or dynamic changes.
   *   - NOTE: Debug commands are called but their results are not used, potentially adding unnecessary overhead.
   *   - WARN: Workspaces not present in the hardcoded `workspaceOrder` list will be excluded from the displayed output.
   */
  const loadConfig = useCallback(async () => {
    try {
      setLoading(true);

      // NOTE: Debug: Check database content
      await core.invoke("debug_database_content");

      // NOTE: Debug: Test specific workspace loading
      await core.invoke("debug_workspace_loading");

      const rawData = await core.invoke<Record<string, unknown>>("get_json_config_cmd");

      // NOTE: Extract settings and workspaces from flat structure
      const { settings, ...workspaces } = rawData;

      // NOTE: Define desired sort order - hardcoded workspace names
      // WARN: Workspaces not in this list will be excluded from sorted output
      const workspaceOrder = [
        "Nexus",
        "Investments",
        "Trading",
        "ResponsumOS",
        "Real Estate",
        "TracQ",
      ];

      // NOTE: Sort workspaces according to defined order
      const sortedWorkspaces: Record<string, Record<string, AspectConfig>> = {};
      for (const workspaceName of workspaceOrder) {
        if (workspaces[workspaceName]) {
          sortedWorkspaces[workspaceName] = workspaces[workspaceName] as Record<
            string,
            AspectConfig
          >;
        }
      }

      const data: WorkspaceConfig = {
        settings: (settings && typeof settings === "object"
          ? (settings as WorkspaceConfig["settings"])
          : undefined) || {
          chunk_size: 1,
          daily_capacity_weekday: 5,
          daily_capacity_weekend: 8,
          max_chunk_per_task: 3,
          minimum_chunk_hours: 0.5,
        },
        workspaces: sortedWorkspaces,
      };

      setConfig(data);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Unbekannter Fehler beim Laden der Konfiguration";
      setMessage({
        text: `Fehler beim Laden der Konfiguration: ${message}`,
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * WHAT:
   *   Saves the entire workspace configuration to the Supabase database.
   *
   * WHY:
   *   Persists all workspace and aspect changes to the database for persistence across sessions.
   *
   * SIDE EFFECTS:
   *   - Saves the full configuration object to the database.
   *   - Updates success/error message state.
   *   - Displays a success message on completion.
   *
   * RISKS:
   *   - WARN: Saves the entire config object, which may overwrite other configuration sections if not handled carefully.
   *   - NOTE: There is no validation of the configuration structure before saving.
   *   - WARN: Unsaved changes are lost if the save operation fails.
   */
  const saveConfig = useCallback(async () => {
    if (!config) {
      return;
    }

    try {
      setSaving(true);
      await core.invoke("save_json_config_cmd", { config });
      setMessage({
        text: "Workspace-Konfiguration erfolgreich gespeichert!",
        type: "success",
      });
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Unbekannter Fehler beim Speichern der Konfiguration";
      setMessage({
        text: `Fehler beim Speichern der Konfiguration: ${message}`,
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  }, [config]);

  return {
    config,
    loadConfig,
    loading,
    loadWorkspaceNames,
    message,
    saveConfig,
    saving,
    setConfig,
    setMessage,
    setSaving,
    workspaceNames,
  };
}
